import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'audio[controls]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function focusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.hasAttribute('inert'));
}

/**
 * После смены маршрута переносит фокус на заголовок страницы (h1) внутри
 * контейнера, чтобы скринридер объявил новую страницу. Первый рендер не трогаем:
 * при загрузке фокус остаётся у браузера (skip-link доступна первой по Tab).
 */
export function useRouteFocus(container: RefObject<HTMLElement>) {
  const location = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const root = container.current;
    if (!root) return;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Ленивые страницы и загрузка данных рисуют h1 не сразу — ждём его немного.
    const tryFocus = () => {
      const heading = root.querySelector<HTMLElement>('h1');
      if (heading) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus();
      } else if (attempts++ < 20) {
        timer = setTimeout(tryFocus, 50);
      } else {
        root.focus();
      }
    };
    tryFocus();
    return () => clearTimeout(timer);
  }, [location.pathname, container]);
}

interface DialogProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Описание для aria-describedby (например, текст подтверждения). */
  description?: string;
}

/**
 * Модальный диалог: фокус переходит внутрь, Tab/Shift+Tab не выходят за его
 * пределы, Escape закрывает, после закрытия фокус возвращается на элемент,
 * который открыл диалог.
 */
export function Dialog({ title, onClose, children, description }: DialogProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const root = ref.current;
    if (root) {
      const autofocus = root.querySelector<HTMLElement>('[data-autofocus]');
      (autofocus ?? focusableElements(root)[0] ?? root).focus();
    }
    return () => {
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      closeRef.current();
      return;
    }
    if (e.key !== 'Tab' || !ref.current) return;
    const items = focusableElements(ref.current);
    if (!items.length) {
      e.preventDefault();
      return;
    }
    const firstEl = items[0];
    const lastEl = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === firstEl || active === ref.current)) {
      e.preventDefault();
      lastEl.focus();
    } else if (!e.shiftKey && active === lastEl) {
      e.preventDefault();
      firstEl.focus();
    }
  };

  return (
    <div className="dialog-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className="dialog card"
        onKeyDown={onKeyDown}
      >
        <div className="page-header">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="secondary" onClick={onClose} aria-label={t('common.close')}>
            ×
          </button>
        </div>
        {description && <p id={descId}>{description}</p>}
        {children}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  onConfirm,
  onClose,
}: {
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog title={title} description={message} onClose={onClose}>
      <div className="actions">
        <button type="button" className="secondary" data-autofocus onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="danger"
          onClick={() => {
            onConfirm();
            onClose();
          }}
        >
          {confirmLabel ?? t('common.delete')}
        </button>
      </div>
    </Dialog>
  );
}
